/**
 * useSessionExpiry
 *
 * Polls /api/auth/session-info every 60 seconds. When the session is within
 * 5 minutes of expiring:
 *
 *  - ADMIN users: returns modal state (`showModal`, `msUntilExpiry`) so the
 *    caller can render <AdminReAuthModal> inline without navigating away.
 *  - Regular users: shows a persistent sonner toast with a "Stay signed in"
 *    button that redirects to the OAuth flow.
 *
 * Only active for authenticated users. Non-authenticated users are ignored.
 */
import { getLoginUrl } from "@/const";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const WARN_BEFORE_MS = 5 * 60 * 1000; // 5 minutes
const POLL_INTERVAL_MS = 60 * 1000;   // 60 seconds
const TOAST_ID = "session-expiry-warning";

async function fetchSessionInfo(): Promise<{
  expiresAt: number | null;
  role: string;
} | null> {
  try {
    const res = await fetch("/api/auth/session-info", { credentials: "include" });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export interface SessionExpiryState {
  /** Whether the admin re-auth modal should be shown */
  showModal: boolean;
  /** Milliseconds until session expires (for the modal countdown) */
  msUntilExpiry: number;
  /** Call to hide the modal (used when admin dismisses - triggers logout) */
  dismissModal: () => void;
}

export function useSessionExpiry(
  isAuthenticated: boolean,
  role?: string | null
): SessionExpiryState {
  const toastShownRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [msUntilExpiry, setMsUntilExpiry] = useState(0);

  const dismissModal = () => {
    setShowModal(false);
    // Redirect admin to login page on dismiss
    window.location.href = getLoginUrl();
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    let intervalId: ReturnType<typeof setInterval>;

    const check = async () => {
      const info = await fetchSessionInfo();
      if (!info?.expiresAt) return;

      const ms = info.expiresAt - Date.now();
      const effectiveRole = role ?? info.role;

      if (ms <= 0) {
        // Session already expired
        if (effectiveRole === "admin") {
          setShowModal(false);
        } else {
          toast.dismiss(TOAST_ID);
        }
        window.location.href = getLoginUrl();
        return;
      }

      if (ms <= WARN_BEFORE_MS) {
        if (effectiveRole === "admin") {
          // Admin path: show modal, keep it updated with latest ms
          setMsUntilExpiry(ms);
          setShowModal(true);
          // Dismiss any stale toast that may have been shown before role resolved
          toast.dismiss(TOAST_ID);
          toastShownRef.current = false;
        } else {
          // Regular user path: show sonner toast once
          if (!toastShownRef.current) {
            toastShownRef.current = true;
            const minutes = Math.ceil(ms / 60_000);
            toast.warning(
              `Your session expires in ${minutes} minute${minutes !== 1 ? "s" : ""}.`,
              {
                id: TOAST_ID,
                duration: Infinity,
                action: {
                  label: "Stay signed in",
                  onClick: () => {
                    window.location.href = getLoginUrl();
                  },
                },
              }
            );
          }
        }
      } else {
        // Session was re-issued (sliding window) - clear any warnings
        if (effectiveRole === "admin") {
          setShowModal(false);
          setMsUntilExpiry(0);
        } else if (toastShownRef.current) {
          toastShownRef.current = false;
          toast.dismiss(TOAST_ID);
        }
      }
    };

    // Run immediately, then on interval
    check();
    intervalId = setInterval(check, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      toast.dismiss(TOAST_ID);
    };
  }, [isAuthenticated, role]);

  return { showModal, msUntilExpiry, dismissModal };
}
